import { Analytics } from '@cobliteam/analytics';
import { DeviceType } from '@cobliteam/api-lib/src/__generated__/graphql';
import { type MediaEvent, type MediaView, VideoChannel } from '@cobliteam/common-types';
import { useUpdateFavoriteMedia } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import Alert from '@cobliteam/ui/components/Alert';
import VideoPlayer from '@cobliteam/ui/components/VideoPlayer';
import { cn } from '@cobliteam/ui/utils';
import { useEffect, useMemo, useState } from 'react';
import { MediaTableType } from '../../utils';
import ShareMediaMenuItem from '../ShareMediaMenuItem';

const PREFERRED_VIDEOS_ORDER = [VideoChannel.ROAD, VideoChannel.DRIVER];

export interface CobliCamPlayerProps {
  mediaView?: MediaView;
  hasPrevious?: boolean;
  hasNext?: boolean;
  canNavigate?: boolean;
  onPrevious?: (analyticsProperties?: Record<string, string>) => void;
  onNext?: (analyticsProperties?: Record<string, string>) => void;
}

const CobliCamPlayer = ({ mediaView, hasPrevious, hasNext, onPrevious, onNext }: CobliCamPlayerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const isEvent = mediaView?.mediaInfo.mediaType === 'EVENT_VIDEO';
  const { mutateAsync: toggleFavoriteMutation } = useUpdateFavoriteMedia(isEvent ? MediaTableType.EVENT_MEDIA : MediaTableType.FOOTAGE_MEDIA);

  const isFavorite = mediaView?.mediaInfo.isFavorite;
  const { data: devices } = useGetDevices({});
  const deviceType = devices?.find((device) => device.id === mediaView?.mediaInfo.deviceId)?.type;
  const isCobliCamMulti = deviceType === DeviceType.Multicam;

  const medias = useMemo(() => {
    // The player requires at least one media.
    setIsLoading(true);
    if (!mediaView) return [{ src: '' }];

    return mediaView?.videos
      .sort((one, other) => {
        let oneIndex = PREFERRED_VIDEOS_ORDER.indexOf(one.channel);
        let otherIndex = PREFERRED_VIDEOS_ORDER.indexOf(other.channel);

        oneIndex = oneIndex >= 0 ? oneIndex : 1;
        otherIndex = otherIndex >= 0 ? otherIndex : 1;

        return oneIndex - otherIndex;
      })
      .map((video) => {
        const downloadFileName = [mediaView.mediaInfo.mediaType, mediaView.mediaInfo.licensePlate, video.channel].filter(Boolean).join('_').concat('.mp4');

        return {
          src: video.url,
          downloadFileName,
        };
      });
  }, [mediaView]);

  const onFavoriteToggle = async () => {
    if (!mediaView) return;

    Analytics.track('Favorite Media Button Clicked', {
      newFavoriteStatus: !isFavorite,
      page: window.location.pathname,
    });

    const data = {
      mediaId: mediaView.mediaInfo.id,
      isFavorite: !isFavorite,
    };

    try {
      toggleFavoriteMutation({ updateUserFavoriteMedia: data });
      Alert.success(`Vídeo ${isFavorite ? 'desfavoritado ' : 'favoritado'} com sucesso!`);
    } catch (error) {
      Alert.error(`Ops! Houve um problema ao ${isFavorite ? 'desfavoritar ' : 'favoritar'} o vídeo. Tente novamente.`);
    }
  };

  const goToPrevious = onPrevious
    ? () => {
        onPrevious({ component: 'VideoPlayer' });
      }
    : undefined;

  const goToNext = onNext
    ? () => {
        onNext({ component: 'VideoPlayer' });
      }
    : undefined;

  const trackEvent = (eventName: string) => {
    const { mediaType, mediaDetails, isFavorite, severity } = mediaView?.mediaInfo || {};
    const eventType = isEvent ? (mediaDetails as MediaEvent).eventTrigger : mediaType;

    Analytics.track(eventName, {
      eventType,
      eventSeverity: severity,
      isFavorite,
    });
  };

  const trackWatched = () => {
    trackEvent('Dashcam Video Watched Fully');
  };

  const trackPlayed = () => {
    trackEvent('Dashcam Video Played');
  };

  const trackDownloaded = () => {
    trackEvent('Media Download Button Clicked');
  };

  useEffect(() => {
    if (!(medias.length === 1 && medias[0].src === '')) {
      setIsLoading(false);
    }
  }, [medias]);

  return (
    <VideoPlayer
      className={cn({ 'aspect-[1.554]': !isCobliCamMulti })}
      medias={medias}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      isFavorite={isFavorite}
      onPrevious={goToPrevious}
      onNext={goToNext}
      onFavoriteToggle={onFavoriteToggle}
      shareDropdown={<ShareMediaMenuItem media={mediaView?.mediaInfo} />}
      onEnded={trackWatched}
      onPlay={trackPlayed}
      onDownloadFinished={trackDownloaded}
      isLoading={isLoading}
      isMultiInteraction={isCobliCamMulti}
      downloadable
    />
  );
};

export default CobliCamPlayer;
